import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { supportAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
  Headphones, 
  Send, 
  Loader2,
  MessageSquare,
  HelpCircle,
  CreditCard,
  Car,
  Truck,
  User
} from 'lucide-react';

const CATEGORIES = [
  { value: 'general', label: 'Consulta General', icon: HelpCircle },
  { value: 'ride', label: 'Viajes', icon: Car },
  { value: 'delivery', label: 'Envíos', icon: Truck },
  { value: 'payment', label: 'Pagos y Wallet', icon: CreditCard },
  { value: 'account', label: 'Mi Cuenta', icon: User },
];

const PRIORITIES = [
  { value: 'low', label: 'Baja' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'Alta' },
];

export const SupportButton = ({ variant = 'default', className = '' }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    category: 'general',
    priority: 'normal',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.message.trim()) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      const response = await supportAPI.createTicket(formData);
      if (response.data.success) {
        toast.success('Ticket de soporte creado exitosamente', {
          description: 'Un agente te contactará pronto.'
        });
        setOpen(false);
        setFormData({
          subject: '',
          message: '',
          category: 'general',
          priority: 'normal',
        });
      }
    } catch (error) {
      console.error('Error creating support ticket:', error);
      toast.error('Error al crear el ticket', {
        description: error.response?.data?.detail || 'Intenta nuevamente'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Floating button variant
  if (variant === 'floating') {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button
            data-testid="support-floating-btn"
            className={`fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#00BFA6] hover:bg-[#00887A] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group ${className}`}
          >
            <Headphones className="w-6 h-6 group-hover:scale-110 transition-transform" />
          </button>
        </DialogTrigger>
        <SupportModal 
          formData={formData} 
          handleChange={handleChange} 
          handleSubmit={handleSubmit} 
          loading={loading} 
        />
      </Dialog>
    );
  }

  // Card variant for homepage
  if (variant === 'card') {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button
            data-testid="support-card-btn"
            className={`card-elevated p-6 flex items-start gap-4 w-full text-left hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group cursor-pointer ${className}`}
          >
            <div className="w-12 h-12 rounded-xl bg-[#00BFA6]/10 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
              <Headphones className="w-6 h-6 text-[#00BFA6]" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 mb-1">¿Necesitas ayuda?</h4>
              <p className="text-slate-500 text-sm">Nuestro equipo de soporte está disponible 24/7 para asistirte</p>
            </div>
          </button>
        </DialogTrigger>
        <SupportModal 
          formData={formData} 
          handleChange={handleChange} 
          handleSubmit={handleSubmit} 
          loading={loading} 
        />
      </Dialog>
    );
  }

  // Default button variant
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          data-testid="support-btn"
          className={`border-[#00BFA6] text-[#00BFA6] hover:bg-[#00BFA6]/10 ${className}`}
        >
          <Headphones className="w-4 h-4 mr-2" />
          Soporte
        </Button>
      </DialogTrigger>
      <SupportModal 
        formData={formData} 
        handleChange={handleChange} 
        handleSubmit={handleSubmit} 
        loading={loading} 
      />
    </Dialog>
  );
};

// Separate Modal component for reusability
const SupportModal = ({ formData, handleChange, handleSubmit, loading }) => (
  <DialogContent className="sm:max-w-[500px]">
    <DialogHeader>
      <DialogTitle className="flex items-center gap-2 text-xl font-['Outfit']">
        <div className="w-10 h-10 rounded-xl bg-[#00BFA6]/10 flex items-center justify-center">
          <MessageSquare className="w-5 h-5 text-[#00BFA6]" />
        </div>
        Centro de Soporte
      </DialogTitle>
      <DialogDescription>
        Cuéntanos cómo podemos ayudarte. Un agente te contactará lo antes posible.
      </DialogDescription>
    </DialogHeader>

    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
      {/* Category */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Categoría
        </label>
        <Select
          value={formData.category}
          onValueChange={(value) => handleChange('category', value)}
        >
          <SelectTrigger data-testid="support-category-select">
            <SelectValue placeholder="Selecciona una categoría" />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>
                <div className="flex items-center gap-2">
                  <cat.icon className="w-4 h-4" />
                  {cat.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Subject */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Asunto
        </label>
        <Input
          data-testid="support-subject-input"
          placeholder="¿En qué podemos ayudarte?"
          value={formData.subject}
          onChange={(e) => handleChange('subject', e.target.value)}
          className="h-11"
        />
      </div>

      {/* Message */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Mensaje
        </label>
        <Textarea
          data-testid="support-message-input"
          placeholder="Describe tu problema o consulta con el mayor detalle posible..."
          value={formData.message}
          onChange={(e) => handleChange('message', e.target.value)}
          className="min-h-[120px] resize-none"
        />
      </div>

      {/* Priority */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Prioridad
        </label>
        <Select
          value={formData.priority}
          onValueChange={(value) => handleChange('priority', value)}
        >
          <SelectTrigger data-testid="support-priority-select">
            <SelectValue placeholder="Selecciona prioridad" />
          </SelectTrigger>
          <SelectContent>
            {PRIORITIES.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DialogFooter className="mt-6">
        <Button
          type="submit"
          data-testid="support-submit-btn"
          disabled={loading}
          className="w-full h-12 bg-[#00BFA6] hover:bg-[#00887A] text-white rounded-xl"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Enviando...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Enviar Ticket
            </>
          )}
        </Button>
      </DialogFooter>
    </form>

    {/* Security Note */}
    <p className="text-xs text-slate-400 text-center mt-4">
      Tu solicitud será atendida por nuestro equipo de soporte certificado.
      Tiempo promedio de respuesta: menos de 30 minutos.
    </p>
  </DialogContent>
);

export default SupportButton;
